import { FormEvent, ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '@/components/ui/Modal';
import {
  useCancelLoyaltyCreditMutation,
  useCreditLoyaltyBillMutation,
  useListLoyaltyBillsQuery,
  useLookupLoyaltyCodeMutation,
  useOfferLoyaltyRewardMutation,
  useRedeemLoyaltyCouponMutation,
  type WaiterLoyaltyBill,
  type WaiterLoyaltyCoupon,
  type WaiterLoyaltyCreditCancelled,
  type WaiterLoyaltyCredited,
  type WaiterLoyaltyLookup,
  type WaiterLoyaltyRedeemed,
} from '../waiterApi';
import { formatTicketTime } from '../ticketTime';
import QrCamera from './QrCamera';
import {
  billAmountLabel,
  formatLoyaltyDate,
  isLoyaltyCode,
  isolateLtr,
  loyaltyCreditError,
  loyaltyReasonError,
  loyaltyScanError,
  loyaltyValueLabel,
  normalizeLoyaltyCode,
  type LoyaltyScanError,
} from './loyaltyScanFormat';

interface LoyaltyScanSheetProps {
  open: boolean;
  onClose: () => void;
  token: string;
}

/**
 * Fidélité — le « Scanner » du carnet serveur : le client montre le QR de sa
 * carte (ou d'un avantage déjà débloqué), le serveur le lit et le valide à
 * table. Le pendant du scanner owner/admin (`loyalty/scan`), réduit à ce qui
 * se fait au moment de l'addition : valider un avantage, offrir une récompense
 * avec les points, créditer l'addition d'une table (SC-1638). Le serveur ne
 * saisit jamais de montant : il choisit une addition CLÔTURÉE, sans commande
 * à valider, de la liste (montant définitif — une note ouverte peut encore
 * grossir ou être réunie à une autre, une tournée en attente être refusée),
 * sale_api la chiffre, et une addition ne crédite qu'une carte.
 * Même barème et même annulation (30 min) qu'au scanner du gérant.
 *
 * Le corps n'est monté que feuille ouverte : chaque ouverture repart de la
 * caméra, sans le client précédent.
 */
export default function LoyaltyScanSheet({ open, onClose, token }: LoyaltyScanSheetProps) {
  const { t } = useTranslation();
  return (
    <Modal open={open} onClose={onClose} ariaLabel={t('waiter.loyaltyScan.title')} instant dismissOnBackdrop={false}>
      {open && <LoyaltyScanBody token={token} onClose={onClose} />}
    </Modal>
  );
}

type Phase =
  | { name: 'scan' }
  | { name: 'looking' }
  | { name: 'result'; lookup: WaiterLoyaltyLookup }
  | { name: 'success'; kind: 'coupon' | 'offer'; redeemed: WaiterLoyaltyRedeemed }
  | {
      name: 'credited';
      cardCode: string;
      bill: WaiterLoyaltyBill;
      result: WaiterLoyaltyCredited;
      /** Rempli une fois « Annuler ce crédit » accepté. */
      cancelled: WaiterLoyaltyCreditCancelled | null;
    };

/** SC-1638 — combien de temps un « Confirmer » qui vient d'apparaître reste
 *  inerte : un double tap, ou un pouce déjà en route, ne doit pas répondre à
 *  la question (même règle que le « Plus tard » des notes, SC-1466). */
const CONFIRM_ARM_MS = 600;

/** Vrai CONFIRM_ARM_MS après que `asked` est apparu. Chaque question est un
 *  nouvel objet : la reposer réarme le délai, même sur la même ligne. */
function useArmed(asked: object | null): boolean {
  const [armedFor, setArmedFor] = useState<object | null>(null);
  useEffect(() => {
    if (asked === null) return;
    const id = window.setTimeout(() => setArmedFor(asked), CONFIRM_ARM_MS);
    return () => window.clearTimeout(id);
  }, [asked]);
  return asked !== null && armedFor === asked;
}

/** Ce que le carnet écrit d'une addition : « Table 5+6 », ou le nom de la note. */
function useBillName() {
  const { t } = useTranslation();
  return (bill: WaiterLoyaltyBill) =>
    bill.kind === 'takeaway' ? bill.label : t('waiter.loyaltyScan.credit.table', { number: bill.label });
}

function LoyaltyScanBody({ token, onClose }: { token: string; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'fr';
  const [phase, setPhase] = useState<Phase>({ name: 'scan' });
  const [cameraFailure, setCameraFailure] = useState<'denied' | 'unavailable' | null>(null);
  const [typed, setTyped] = useState('');
  const [entryError, setEntryError] = useState<LoyaltyScanError | null>(null);
  const [actionError, setActionError] = useState<LoyaltyScanError | null>(null);
  // « Offrir » demande un second geste, au même endroit : le bouton devient
  // « Confirmer (−N pts) » sur sa propre ligne.
  const [confirming, setConfirming] = useState<string | null>(null);
  // SC-1638 — même règle pour « Créditer » une addition et « Annuler ce
  // crédit » ; le refus s'affiche dans la section, sous le pouce du serveur.
  const [confirmingBill, setConfirmingBill] = useState<{ sessionUuid: string } | null>(null);
  const [confirmingUndo, setConfirmingUndo] = useState<object | null>(null);
  const [creditError, setCreditError] = useState<LoyaltyScanError | null>(null);
  const [lookupCode] = useLookupLoyaltyCodeMutation();
  const [redeemCoupon, { isLoading: redeemingCoupon }] = useRedeemLoyaltyCouponMutation();
  const [offerReward, { isLoading: offering }] = useOfferLoyaltyRewardMutation();
  const [creditBill, { isLoading: crediting }] = useCreditLoyaltyBillMutation();
  const [cancelCredit, { isLoading: cancellingCredit }] = useCancelLoyaltyCreditMutation();
  const busy = redeemingCoupon || offering || crediting || cancellingCredit;
  const undoArmed = useArmed(confirmingUndo);
  const billName = useBillName();

  const backToScan = () => {
    setPhase({ name: 'scan' });
    setTyped('');
    setEntryError(null);
    setActionError(null);
    setConfirming(null);
    setConfirmingBill(null);
    setConfirmingUndo(null);
    setCreditError(null);
  };

  // `silent` : relire le client déjà à l'écran après un refus (le solde ou
  // le stock a pu bouger), sans l'effacer ni effacer le message du refus.
  const runLookup = async (raw: string, silent = false) => {
    const code = normalizeLoyaltyCode(raw);
    if (!isLoyaltyCode(code)) {
      if (!silent) {
        setEntryError('not_found');
        setPhase({ name: 'scan' });
      }
      return;
    }
    if (!silent) {
      setEntryError(null);
      setActionError(null);
      setConfirming(null);
      setPhase({ name: 'looking' });
    }
    try {
      const lookup = await lookupCode({ code, token }).unwrap();
      setConfirming(null);
      setPhase({ name: 'result', lookup });
    } catch (error) {
      if (silent) return;
      setEntryError(loyaltyScanError(error));
      setPhase({ name: 'scan' });
    }
  };

  const handleManualSubmit = (event: FormEvent) => {
    event.preventDefault();
    void runLookup(typed);
  };

  const handleValidateCoupon = async (code: string) => {
    if (busy) return;
    setActionError(null);
    try {
      const redeemed = await redeemCoupon({ code, token }).unwrap();
      setPhase({ name: 'success', kind: 'coupon', redeemed });
    } catch (error) {
      setActionError(loyaltyScanError(error));
      if (phase.name === 'result' && phase.lookup.kind === 'member') {
        void runLookup(phase.lookup.cardCode, true);
      }
    }
  };

  const handleOffer = async (cardCode: string, rewardUuid: string) => {
    if (busy) return;
    setActionError(null);
    try {
      const redeemed = await offerReward({ cardCode, rewardUuid, token }).unwrap();
      setPhase({ name: 'success', kind: 'offer', redeemed });
    } catch (error) {
      setActionError(loyaltyScanError(error));
      void runLookup(cardCode, true);
    }
  };

  // Faux = refusé : la section relit alors la liste (l'addition a pu fermer,
  // grossir, ou partir sur une autre carte).
  const handleCredit = async (cardCode: string, bill: WaiterLoyaltyBill): Promise<boolean> => {
    if (busy) return true;
    setCreditError(null);
    try {
      const result = await creditBill({ cardCode, sessionUuid: bill.sessionUuid, token }).unwrap();
      setConfirmingBill(null);
      setPhase({ name: 'credited', cardCode, bill, result, cancelled: null });
      return true;
    } catch (error) {
      setConfirmingBill(null);
      setCreditError(loyaltyCreditError(error));
      void runLookup(cardCode, true);
      return false;
    }
  };

  const handleUndoCredit = async () => {
    if (busy || phase.name !== 'credited') return;
    setCreditError(null);
    try {
      const cancelled = await cancelCredit({
        cardCode: phase.cardCode,
        sessionUuid: phase.bill.sessionUuid,
        token,
      }).unwrap();
      setConfirmingUndo(null);
      setPhase({ ...phase, cancelled });
    } catch (error) {
      setConfirmingUndo(null);
      setCreditError(loyaltyCreditError(error));
    }
  };

  const header = (
    <div className="shrink-0 flex items-center justify-between gap-2 px-5 pt-4 pb-2">
      <h2 className="font-sans text-[18px] font-bold text-ink">{t('waiter.loyaltyScan.title')}</h2>
      <button
        type="button"
        data-testid="loyalty-scan-close"
        aria-label={t('common.close')}
        onClick={onClose}
        className="-me-2 w-11 h-11 shrink-0 flex items-center justify-center font-sans text-2xl leading-none text-ink"
      >
        ×
      </button>
    </div>
  );

  const errorBanner = (error: LoyaltyScanError | null, testId: string) =>
    error ? (
      <p
        data-testid={testId}
        data-error={error}
        role="alert"
        className="rounded-btn px-3 py-2 font-sans text-base font-bold text-white"
        style={{ backgroundColor: 'var(--w-danger)' }}
      >
        {t(`waiter.loyaltyScan.errors.${error}`)}
      </p>
    ) : null;

  const secondaryButton = (label: string, onClick: () => void, testId: string) => (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className="h-12 px-4 rounded-btn font-sans text-[18px] font-bold underline text-ink"
    >
      {label}
    </button>
  );

  const primaryButton = (label: string, onClick: () => void, testId: string) => (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className="h-12 px-6 bg-ink text-paper rounded-btn font-sans text-[18px] font-bold"
    >
      {label}
    </button>
  );

  const footer = (children: ReactNode) => (
    <div className="shrink-0 flex justify-end gap-3 px-5 py-3 border-t-2 border-hairline">{children}</div>
  );

  if (phase.name === 'credited') {
    const { bill, result, cancelled } = phase;
    return (
      <div
        data-testid="loyalty-scan-credited"
        data-state={cancelled ? 'cancelled' : 'credited'}
        className="flex flex-col min-h-0"
      >
        {header}
        <div className="min-h-0 overflow-y-auto px-5 pb-4 flex flex-col items-center gap-3 text-center">
          <span
            aria-hidden="true"
            className={`w-16 h-16 rounded-full flex items-center justify-center text-3xl font-bold ${
              cancelled ? 'bg-ink text-paper' : 'text-white'
            }`}
            style={cancelled ? undefined : { backgroundColor: 'var(--w-success)' }}
          >
            {cancelled ? '↺' : '✓'}
          </span>
          <p className="font-sans text-[20px] font-bold text-ink">
            {t(
              cancelled
                ? 'waiter.loyaltyScan.credit.cancelled'
                : result.alreadyCredited
                  ? 'waiter.loyaltyScan.credit.already'
                  : 'waiter.loyaltyScan.credit.success',
            )}
          </p>
          <p data-testid="loyalty-scan-credited-points" className="font-sans text-[26px] font-bold text-ink">
            {cancelled
              ? t('waiter.loyaltyScan.credit.cancelledPoints', { n: cancelled.cancelled })
              : t('waiter.loyaltyScan.credit.points', { n: result.credited })}
          </p>
          <p dir="auto" className="font-sans text-base text-ink">
            {/* Le montant crédité : une table ouverte a pu commander depuis la liste. */}
            {billName(bill)} · <bdi dir="ltr">{billAmountLabel(result.amount ?? bill.amount)}</bdi>
          </p>
          <p className="font-sans text-base text-ink-mute">
            {t('waiter.loyaltyScan.success.balance', { n: cancelled ? cancelled.balance : result.balance })}
          </p>
          {!cancelled && (
            <div className="w-full flex flex-col items-center gap-2 pt-2">
              {errorBanner(creditError, 'loyalty-scan-credit-error')}
              {confirmingUndo ? (
                <div className="w-full flex flex-wrap items-center justify-end gap-2">
                  <p className="me-auto font-sans text-base font-bold text-ink">
                    {t('waiter.loyaltyScan.credit.undoQuestion', { n: result.credited })}
                  </p>
                  <button
                    type="button"
                    data-testid="loyalty-scan-credit-undo-keep"
                    onClick={() => setConfirmingUndo(null)}
                    className="h-11 px-3 rounded-btn font-sans text-base font-bold underline text-ink"
                  >
                    {t('waiter.loyaltyScan.credit.undoKeep')}
                  </button>
                  <button
                    type="button"
                    data-testid="loyalty-scan-credit-undo-confirm"
                    disabled={busy || !undoArmed}
                    onClick={() => void handleUndoCredit()}
                    className="h-11 px-4 rounded-btn font-sans text-base font-bold text-white disabled:opacity-60"
                    style={{ backgroundColor: 'var(--w-danger)' }}
                  >
                    {t('waiter.loyaltyScan.credit.undoConfirm')}
                  </button>
                </div>
              ) : (
                <>
                  <button
                    type="button"
                    data-testid="loyalty-scan-credit-undo"
                    disabled={busy}
                    onClick={() => setConfirmingUndo({})}
                    className="h-11 px-3 rounded-btn font-sans text-base font-bold underline text-ink disabled:opacity-60"
                  >
                    {t('waiter.loyaltyScan.credit.undo')}
                  </button>
                  <p className="font-sans text-sm text-ink-mute">{t('waiter.loyaltyScan.credit.undoHint')}</p>
                </>
              )}
            </div>
          )}
        </div>
        {footer(
          <>
            {secondaryButton(t('common.close'), onClose, 'loyalty-scan-done')}
            {primaryButton(t('waiter.loyaltyScan.next'), backToScan, 'loyalty-scan-next')}
          </>,
        )}
      </div>
    );
  }

  if (phase.name === 'success') {
    const { redeemed, kind } = phase;
    const valueLabel = loyaltyValueLabel(redeemed.rewardType, redeemed.value, locale, false);
    return (
      <div data-testid="loyalty-scan-success" data-kind={kind} className="flex flex-col min-h-0">
        {header}
        <div className="min-h-0 overflow-y-auto px-5 pb-4 flex flex-col items-center gap-3 text-center">
          <span
            aria-hidden="true"
            className="w-16 h-16 rounded-full flex items-center justify-center text-white text-3xl font-bold"
            style={{ backgroundColor: 'var(--w-success)' }}
          >
            ✓
          </span>
          <p className="font-sans text-[20px] font-bold text-ink">
            {t(kind === 'coupon' ? 'waiter.loyaltyScan.success.coupon' : 'waiter.loyaltyScan.success.offer')}
          </p>
          <p className="font-sans text-[18px] text-ink">{redeemed.rewardName}</p>
          <p data-testid="loyalty-scan-instruction" className="font-sans text-[22px] font-bold text-ink">
            {valueLabel
              ? t('waiter.loyaltyScan.success.discount', { value: isolateLtr(valueLabel) })
              : t('waiter.loyaltyScan.success.item')}
          </p>
          {redeemed.balance !== null && (
            <p className="font-sans text-base text-ink-mute">
              {t('waiter.loyaltyScan.success.balance', { n: redeemed.balance })}
            </p>
          )}
        </div>
        {footer(
          <>
            {secondaryButton(t('common.close'), onClose, 'loyalty-scan-done')}
            {primaryButton(t('waiter.loyaltyScan.next'), backToScan, 'loyalty-scan-next')}
          </>,
        )}
      </div>
    );
  }

  if (phase.name === 'result') {
    const { lookup } = phase;
    return (
      <div data-testid="loyalty-scan-result" data-kind={lookup.kind} className="flex flex-col min-h-0">
        {header}
        <div className="min-h-0 overflow-y-auto px-5 pb-4 flex flex-col gap-3">
          {errorBanner(actionError, 'loyalty-scan-action-error')}
          {lookup.kind === 'member' ? (
            <MemberView
              lookup={lookup}
              locale={locale}
              busy={busy}
              confirming={confirming}
              onConfirm={setConfirming}
              onValidateCoupon={(code) => void handleValidateCoupon(code)}
              onOffer={(uuid) => void handleOffer(lookup.cardCode, uuid)}
              credit={
                <CreditBillSection
                  token={token}
                  busy={busy}
                  asked={confirmingBill}
                  onAsk={setConfirmingBill}
                  onCredit={(bill) => handleCredit(lookup.cardCode, bill)}
                  error={errorBanner(creditError, 'loyalty-scan-credit-error')}
                />
              }
            />
          ) : lookup.valid ? (
            <CouponCard coupon={lookup.coupon} memberName={lookup.member?.displayName ?? ''} locale={locale} />
          ) : (
            <div
              data-testid="loyalty-scan-coupon-invalid"
              role="alert"
              className="rounded-btn border-2 px-4 py-6 text-center flex flex-col gap-1"
              style={{ borderColor: 'var(--w-danger)', color: 'var(--w-danger)' }}
            >
              <p className="font-sans text-[20px] font-bold">
                {t(`waiter.loyaltyScan.errors.${loyaltyReasonError(lookup.reason)}`)}
              </p>
              {lookup.reason === 'already_used' && lookup.redeemedAt && (
                <p className="font-sans text-base font-bold">
                  {t('waiter.loyaltyScan.coupon.usedOn', { date: formatLoyaltyDate(lookup.redeemedAt, locale) })}
                </p>
              )}
              {lookup.member && <p className="font-sans text-base">{lookup.member.displayName}</p>}
            </div>
          )}
        </div>
        {footer(
          lookup.kind === 'coupon' && lookup.valid ? (
            <>
              {secondaryButton(t('waiter.loyaltyScan.another'), backToScan, 'loyalty-scan-another')}
              <button
                type="button"
                data-testid="loyalty-scan-coupon-validate"
                disabled={busy}
                onClick={() => void handleValidateCoupon(lookup.coupon.code)}
                className="h-12 px-6 rounded-btn font-sans text-[18px] font-bold text-white disabled:opacity-60"
                style={{ backgroundColor: 'var(--w-success)' }}
              >
                {t('waiter.loyaltyScan.validate')}
              </button>
            </>
          ) : (
            primaryButton(t('waiter.loyaltyScan.another'), backToScan, 'loyalty-scan-another')
          ),
        )}
      </div>
    );
  }

  return (
    <div data-testid="loyalty-scan-sheet" className="flex flex-col min-h-0">
      {header}
      <div className="min-h-0 overflow-y-auto px-5 pb-5 flex flex-col gap-3">
        {errorBanner(entryError, 'loyalty-scan-entry-error')}
        {phase.name === 'looking' ? (
          <p role="status" className="py-10 text-center font-sans text-[18px] font-bold text-ink">
            {t('waiter.loyaltyScan.looking')}
          </p>
        ) : cameraFailure ? (
          <p data-testid="loyalty-scan-camera-failure" className="font-sans text-base text-ink">
            {t(`waiter.loyaltyScan.camera.${cameraFailure}`)}
          </p>
        ) : (
          <>
            <QrCamera onDecode={(text) => void runLookup(text)} onFailure={setCameraFailure} />
            <p className="font-sans text-base text-ink-mute text-center">{t('waiter.loyaltyScan.camera.hint')}</p>
          </>
        )}
        {phase.name === 'scan' && (
          <form onSubmit={handleManualSubmit} className="flex flex-col gap-2">
            <label htmlFor="loyalty-scan-code" className="font-sans text-base font-bold text-ink">
              {/* « Ou saisir » n'a de sens qu'à côté d'une caméra. */}
              {t(cameraFailure ? 'waiter.loyaltyScan.manual.labelAlone' : 'waiter.loyaltyScan.manual.label')}
            </label>
            {/* Le bouton passe sous le champ quand la feuille est étroite (320px) :
                un code de 12 signes doit rester lisible en entier. */}
            <div className="flex flex-wrap gap-2">
              <input
                id="loyalty-scan-code"
                data-testid="loyalty-scan-code"
                value={typed}
                onChange={(event) => setTyped(event.target.value)}
                autoCapitalize="characters"
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                placeholder="K7M2-QX9P-AB"
                className="min-w-[10rem] flex-1 h-12 rounded-btn border-2 border-ink bg-paper px-3 font-sans text-[18px] text-ink uppercase"
              />
              <button
                type="submit"
                data-testid="loyalty-scan-submit"
                disabled={normalizeLoyaltyCode(typed) === ''}
                className="shrink-0 ms-auto h-12 px-5 bg-ink text-paper rounded-btn font-sans text-[18px] font-bold disabled:opacity-60"
              >
                {t('waiter.loyaltyScan.manual.submit')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function CouponCard({ coupon, memberName, locale }: { coupon: WaiterLoyaltyCoupon; memberName: string; locale: string }) {
  const { t } = useTranslation();
  const valueLabel = loyaltyValueLabel(coupon.rewardType, coupon.value, locale);
  return (
    <div data-testid="loyalty-scan-coupon" className="rounded-btn border-2 border-ink p-4 flex flex-col gap-2">
      <p className="font-sans text-sm font-bold uppercase text-ink-mute">{t('waiter.loyaltyScan.coupon.title')}</p>
      <p className="font-sans text-[22px] font-bold leading-tight text-ink">{coupon.rewardName}</p>
      {valueLabel && (
        <p className="font-sans text-[26px] font-bold text-ink">
          <bdi dir="ltr">{valueLabel}</bdi>
        </p>
      )}
      {memberName && <p className="font-sans text-base text-ink">{memberName}</p>}
      {coupon.expiresAt && (
        <p className="font-sans text-base text-ink-mute">
          {t('waiter.loyaltyScan.coupon.until', { date: formatLoyaltyDate(coupon.expiresAt, locale) })}
        </p>
      )}
    </div>
  );
}

interface MemberViewProps {
  lookup: Extract<WaiterLoyaltyLookup, { kind: 'member' }>;
  locale: string;
  busy: boolean;
  confirming: string | null;
  onConfirm: (rewardUuid: string | null) => void;
  onValidateCoupon: (code: string) => void;
  onOffer: (rewardUuid: string) => void;
  /** SC-1638 — « Créditer une addition », sous tout le reste. */
  credit: ReactNode;
}

function MemberView({
  lookup,
  locale,
  busy,
  confirming,
  onConfirm,
  onValidateCoupon,
  onOffer,
  credit,
}: MemberViewProps) {
  const { t } = useTranslation();
  const { member } = lookup;
  // Programme éteint : on montre le client, rien ne s'offre ni ne se valide
  // d'ici (même règle que le scanner owner/admin).
  const actions = lookup.enabled;
  return (
    <div data-testid="loyalty-scan-member" className="flex flex-col gap-3">
      <div className="rounded-btn bg-ink text-paper p-4 flex items-baseline justify-between gap-3">
        <p dir="auto" className="min-w-0 truncate font-sans text-[18px] font-bold">
          {member.displayName}
        </p>
        <p data-testid="loyalty-scan-balance" className="shrink-0 font-sans text-[26px] font-bold tabular-nums">
          {t('waiter.loyaltyScan.points', { n: member.balance })}
        </p>
      </div>

      {!actions && (
        <p data-testid="loyalty-scan-disabled" className="font-sans text-base font-bold text-ink">
          {t('waiter.loyaltyScan.errors.disabled')}
        </p>
      )}

      {lookup.coupons.length > 0 && (
        <section className="flex flex-col gap-2">
          <h3 className="font-sans text-base font-bold text-ink">{t('waiter.loyaltyScan.member.coupons')}</h3>
          <ul className="flex flex-col gap-2">
            {lookup.coupons.map((coupon) => {
              const valueLabel = loyaltyValueLabel(coupon.rewardType, coupon.value, locale);
              return (
                <li
                  key={coupon.code}
                  data-testid={`loyalty-scan-wallet-${coupon.code}`}
                  className="flex items-center gap-3 rounded-btn border-2 p-3"
                  style={{ borderColor: 'var(--w-success)' }}
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-sans text-base font-bold text-ink">{coupon.rewardName}</p>
                    <p className="font-sans text-sm text-ink">
                      {valueLabel && (
                        <span className="font-bold">
                          <bdi dir="ltr">{valueLabel}</bdi> ·{' '}
                        </span>
                      )}
                      {coupon.expiresAt &&
                        t('waiter.loyaltyScan.coupon.until', { date: formatLoyaltyDate(coupon.expiresAt, locale) })}
                    </p>
                  </div>
                  {actions && (
                    <button
                      type="button"
                      data-testid={`loyalty-scan-wallet-validate-${coupon.code}`}
                      disabled={busy}
                      onClick={() => onValidateCoupon(coupon.code)}
                      className="shrink-0 h-11 px-4 rounded-btn font-sans text-base font-bold text-white disabled:opacity-60"
                      style={{ backgroundColor: 'var(--w-success)' }}
                    >
                      {t('waiter.loyaltyScan.validate')}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <section className="flex flex-col gap-2">
        <h3 className="font-sans text-base font-bold text-ink">{t('waiter.loyaltyScan.member.rewards')}</h3>
        {lookup.rewards.length === 0 ? (
          <p className="font-sans text-base text-ink-mute">{t('waiter.loyaltyScan.member.rewardsEmpty')}</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {lookup.rewards.map((reward) => {
              const valueLabel = loyaltyValueLabel(reward.type, reward.value, locale);
              return (
                <li
                  key={reward.uuid}
                  data-testid={`loyalty-scan-reward-${reward.uuid}`}
                  // Bord noir = offrable, comme partout dans le carnet ; le bord
                  // pâle est réservé à ce qui n'est pas encore à portée.
                  className={`flex flex-wrap items-center gap-x-3 gap-y-2 rounded-btn border-2 p-3 ${
                    reward.affordable ? 'border-ink' : 'border-hairline'
                  }`}
                >
                  {/* Un plancher de largeur : sous lui, l'état (« Encore N pts »,
                      « Offrir ») passe à la ligne au lieu d'écraser le nom. */}
                  <div className="min-w-[9rem] flex-1">
                    <p className="font-sans text-base font-bold text-ink">{reward.name}</p>
                    <p className="font-sans text-sm text-ink">
                      {t('waiter.loyaltyScan.points', { n: reward.pointsCost })}
                      {valueLabel && (
                        <span className="font-bold">
                          {' '}
                          · <bdi dir="ltr">{valueLabel}</bdi>
                        </span>
                      )}
                    </p>
                  </div>
                  {!reward.affordable ? (
                    <span
                      data-testid={`loyalty-scan-missing-${reward.uuid}`}
                      className="shrink-0 ms-auto font-sans text-sm font-bold text-ink-mute"
                    >
                      {t('waiter.loyaltyScan.member.missing', {
                        n: Math.max(0, reward.pointsCost - member.balance),
                      })}
                    </span>
                  ) : !actions ? null : confirming === reward.uuid ? (
                    // Sa propre ligne : à côté du nom, la paire « Annuler |
                    // Confirmer » réduisait le nom à un mot par ligne (revue
                    // design) — on ne confirme pas ce qu'on ne lit plus.
                    <div className="basis-full flex justify-end gap-2">
                      <button
                        type="button"
                        data-testid={`loyalty-scan-offer-cancel-${reward.uuid}`}
                        onClick={() => onConfirm(null)}
                        className="h-11 px-3 rounded-btn font-sans text-base font-bold underline text-ink"
                      >
                        {t('common.cancel')}
                      </button>
                      <button
                        type="button"
                        data-testid={`loyalty-scan-offer-confirm-${reward.uuid}`}
                        disabled={busy}
                        onClick={() => onOffer(reward.uuid)}
                        className="h-11 px-4 rounded-btn font-sans text-base font-bold text-white disabled:opacity-60"
                        style={{ backgroundColor: 'var(--w-success)' }}
                      >
                        {t('waiter.loyaltyScan.offerConfirm', { n: reward.pointsCost })}
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      data-testid={`loyalty-scan-offer-${reward.uuid}`}
                      disabled={busy}
                      onClick={() => onConfirm(reward.uuid)}
                      className="shrink-0 ms-auto h-11 px-4 rounded-btn border-2 border-ink bg-paper font-sans text-base font-bold text-ink disabled:opacity-60"
                    >
                      {t('waiter.loyaltyScan.offer')}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* SC-1638 — en dernier, jamais à la place de « Valider » / « Offrir » :
          les pouces du personnel savent déjà où ils sont. */}
      {actions && credit}
    </div>
  );
}

interface CreditBillSectionProps {
  token: string;
  busy: boolean;
  asked: { sessionUuid: string } | null;
  onAsk: (asked: { sessionUuid: string } | null) => void;
  /** Faux = refusé. */
  onCredit: (bill: WaiterLoyaltyBill) => Promise<boolean>;
  error: ReactNode;
}

/**
 * SC-1638 — les additions que le serveur peut créditer sur cette carte : les
 * tables et notes clôturées depuis peu, la dernière d'abord, sans commande à
 * valider. Une table encore ouverte (ou dont une tournée attend) n'y est pas ;
 * la ligne d'aide le dit, pour qu'on la termine d'abord.
 * Toucher « Créditer » pose la question sur la ligne même (« Créditer
 * 42,50 € ? »), Annuler à gauche, Confirmer à droite — inerte un instant.
 */
function CreditBillSection({ token, busy, asked, onAsk, onCredit, error }: CreditBillSectionProps) {
  const { t } = useTranslation();
  const billName = useBillName();
  const armed = useArmed(asked);
  const { data: bills, isLoading, isError, refetch } = useListLoyaltyBillsQuery(
    { token },
    { refetchOnMountOrArgChange: true },
  );

  const credit = async (bill: WaiterLoyaltyBill) => {
    if (!(await onCredit(bill))) void refetch();
  };

  return (
    <section data-testid="loyalty-scan-credit" className="flex flex-col gap-2">
      <h3 className="font-sans text-base font-bold text-ink">{t('waiter.loyaltyScan.credit.title')}</h3>
      {error}
      {isLoading ? (
        <p className="font-sans text-base text-ink-mute">{t('waiter.loyaltyScan.credit.loading')}</p>
      ) : isError ? (
        <p data-testid="loyalty-scan-credit-load-error" className="font-sans text-base text-ink-mute">
          {t('waiter.loyaltyScan.credit.loadError')}
        </p>
      ) : !bills || bills.length === 0 ? (
        <p data-testid="loyalty-scan-credit-empty" className="font-sans text-base text-ink-mute">
          {t('waiter.loyaltyScan.credit.empty')}
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {bills.map((bill) => {
            const amount = billAmountLabel(bill.amount);
            return (
              <li
                key={bill.sessionUuid}
                data-testid={`loyalty-scan-bill-${bill.sessionUuid}`}
                className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-btn border-2 border-ink p-3"
              >
                <div className="min-w-[9rem] flex-1">
                  <p dir="auto" className="font-sans text-base font-bold text-ink">
                    {billName(bill)}
                  </p>
                  <p className="font-sans text-sm text-ink">
                    <bdi dir="ltr" className="font-bold">
                      {amount}
                    </bdi>
                    {bill.closedAt &&
                      ` · ${t('waiter.loyaltyScan.credit.closedAt', { time: formatTicketTime(bill.closedAt) })}`}
                  </p>
                </div>
                {asked?.sessionUuid === bill.sessionUuid ? (
                  // Sa propre ligne, comme « Offrir » : la question se lit en entier.
                  <div className="basis-full flex flex-wrap items-center justify-end gap-2">
                    <p className="me-auto font-sans text-base font-bold text-ink">
                      {t('waiter.loyaltyScan.credit.confirmQuestion', { amount: isolateLtr(amount) })}
                    </p>
                    <button
                      type="button"
                      data-testid={`loyalty-scan-bill-cancel-${bill.sessionUuid}`}
                      onClick={() => onAsk(null)}
                      className="h-11 px-3 rounded-btn font-sans text-base font-bold underline text-ink"
                    >
                      {t('common.cancel')}
                    </button>
                    <button
                      type="button"
                      data-testid={`loyalty-scan-bill-confirm-${bill.sessionUuid}`}
                      disabled={busy || !armed}
                      onClick={() => void credit(bill)}
                      className="h-11 px-4 rounded-btn font-sans text-base font-bold text-white disabled:opacity-60"
                      style={{ backgroundColor: 'var(--w-success)' }}
                    >
                      {t('waiter.loyaltyScan.credit.confirm')}
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    data-testid={`loyalty-scan-bill-credit-${bill.sessionUuid}`}
                    disabled={busy}
                    onClick={() => onAsk({ sessionUuid: bill.sessionUuid })}
                    className="shrink-0 ms-auto h-11 px-4 rounded-btn border-2 border-ink bg-paper font-sans text-base font-bold text-ink disabled:opacity-60"
                  >
                    {t('waiter.loyaltyScan.credit.action')}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {!isLoading && !isError && (
        // Pourquoi une table manque : elle n'est pas encore clôturée.
        <p data-testid="loyalty-scan-credit-hint" className="font-sans text-sm text-ink-mute">
          {t('waiter.loyaltyScan.credit.hint')}
        </p>
      )}
    </section>
  );
}
